import { z } from "zod";
import {
  BigDecimal,
  Currency,
  Incoterms,
  Instant,
  LocalDate,
  Locale,
  StringMD,
  StringNE,
  WithDbId,
} from "./common.schema.js";
import { Organisation } from "./organisation.schema.js";
import { StorageLocation } from "./storage-location.schema.js";

export const NewDealRequestState = z.enum([
  "newDeal",
  "feroAccepted",
  "feroRejected",
  "orgAccepted",
  "doGenerated",
  "doSigned",
  "feroVoided",
  "orgRejected",
  "cancelled",
  "depositInvoiced",
  "depositPaid",
  "depositPaymentReceived",
  "poCreated",
  "poAccepted",
  "collectionRequested",
  "collectionAccepted",
  "collectionConfirmed",
  "carrierDeliveryConfirmed",
  "warehouseDeliveryConfirmed",
  "stockTransferred",
  "paymentTransferred",
]);
export type NewDealRequestState = z.infer<typeof NewDealRequestState>;

export const NewDealRequestType = z.enum(["newStock", "existingStock"]);
export type NewDealRequestType = z.infer<typeof NewDealRequestType>;

export const NewDealRequestStateEvent = z.object({
  time: Instant,
  state: NewDealRequestState,
});
export type NewDealRequestStateEvent = z.infer<typeof NewDealRequestStateEvent>;

export const MapEntry = <K extends z.ZodType, V extends z.ZodType>(
  key: K,
  value: V,
) =>
  z.object({
    key: key,
    value: value,
  });

export const ForeignCurrencySupplierPaymentValues = z.object({
  dealCurrencyTotalAmountPaid: BigDecimal,
  fxServiceChargePct: BigDecimal,
});
export type ForeignCurrencySupplierPaymentValues = z.infer<
  typeof ForeignCurrencySupplierPaymentValues
>;

export const NewDealRequest = z.object({
  dealNumber: StringNE,
  organisationId: z.string(),
  supplierId: z.string().nullable(),
  carrierId: z.string().nullable(),
  dealId: z.string().nullable(),
  storageLocationId: z.string(),
  dealRequestType: NewDealRequestType,
  state: NewDealRequestState,
  lastStateSet: Instant,
  newDealRequestEvents: z.array(NewDealRequestStateEvent),
  supplierEarliestCollectionDate: LocalDate.nullable(),
  carrierEarliestCollectionDate: LocalDate.nullable(),
  carrierDeliveryEta: LocalDate.nullable(),
  supplierIsPaid: Instant.nullable(),
  carrierIsPaid: Instant.nullable(),
  dealTermsId: z.string().nullable(),
  additionalTermsText: StringMD,
  collectionReminderSentAt: Instant.nullable(),
  createdAt: Instant,
  lossSaleOrderId: z.string().nullable(),
  hasExtraStockDelivered: z.boolean(),
  locale: Locale,
  feroNudgeNotificationsTimestamps: z.array(
    MapEntry(NewDealRequestState, z.array(Instant)),
  ),
  purchaseCurrency: Currency,
  settlementCurrency: Currency,
  depositInvoiceNumber: StringNE.nullable(),
  incoterms: Incoterms.nullable(),
  totalDiscountPct: BigDecimal.nullable(),
  purchaseOrderNumber: z.string().nullable(),
  purchaseBillNumber: z.string().nullable(),
  rejectionReason: StringMD.nullable(),
  foreignCurrencySupplierPayment:
    ForeignCurrencySupplierPaymentValues.nullable(),
  contractNoteId: z.string().nullable(),
  poFileId: z.string().nullable(),
  depositInvoiceId: z.string().nullable(),
  deliverySaleOrderId: z.string().nullable(),
  deliveryLossInvoiceId: z.string().nullable(),
  voidNewDealInvoiceId: z.string().nullable(),
});
export type NewDealRequest = z.infer<typeof NewDealRequest>;

export const StockAdjustmentDetails = z.object({
  reasonForStockAdjustment: z.string().nullable(),
});
export type StockAdjustmentDetails = z.infer<typeof StockAdjustmentDetails>;

export const NewDealRequestSummaryView = z.object({
  organisation: WithDbId(Organisation),
  storageLocation: WithDbId(StorageLocation),
  newDealRequest: WithDbId(NewDealRequest),
  supplierName: z.string().nullable(),
  upcomingStateDeadline: z.string().nullable(),
  stockAdjustmentDetails: StockAdjustmentDetails.nullable(),
});
export type NewDealRequestSummaryView = z.infer<
  typeof NewDealRequestSummaryView
>;

export const NewDealRequestQueryStateFilter = z.union([
  z.literal("all"),
  z.literal("inProgress"),
  z.literal("inFinalState"),
  z.literal("inStates"),
  z.literal("newStockInProgress"),
  z.literal("existingStockInProgress"),
]);
export type NewDealRequestQueryStateFilter = z.infer<
  typeof NewDealRequestQueryStateFilter
>;

export const NewDealRequestQueryReq = z.object({
  stateFilter: NewDealRequestQueryStateFilter,
  states: z.preprocess(
    (input) => (typeof input === "string" ? [] : input),
    z.array(z.string()).default([]),
  ),
  organisations: z.preprocess(
    (input) => (typeof input === "string" ? [] : input),
    z.array(z.string()).default([]),
  ),
  storageLocations: z.preprocess(
    (input) => (typeof input === "string" ? [] : input),
    z.array(z.string()).default([]),
  ),
  offset: z.coerce.number().default(0),
  count: z.coerce.number().default(-1),
});
export type NewDealRequestQueryReq = z.infer<typeof NewDealRequestQueryReq>;
